import { API_ENDPOINTS } from '@/api/endpoints.constant';
import { systemConfig } from '@/config/systemConfig';
import {
  FactoryVisitSession,
  FactoryVisitSessionEvidence,
  FactoryVisitSessionLookupResult,
} from '@/types/LocationVerification.type';
import { clivApiRequest } from '@/utils/apiClient';
import {
  logFactoryVerificationDebug,
  logFactoryVerificationWarning,
} from '@/utils/factoryVerificationLogger.util';
import { factoryVisitSessionStorage } from '@/store/idbStorage/factoryVisitSessionStorage';
import { DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS } from '@/utils/factoryVisitSession.util';

type ServerFactoryVisitSession = {
  id: string;
  factoryId: string;
  inspectorId: string;
  sessionDate: string;
  status: FactoryVisitSession['status'];
  evidence: FactoryVisitSessionEvidence[];
};

type ServerFactoryVisitSessionActiveResponse = {
  reuseDays: number;
  session: ServerFactoryVisitSession | null;
};

async function lookupOfflineSession(params: {
  tenantId: string;
  factoryId: string;
  inspectorId: string;
}): Promise<FactoryVisitSessionLookupResult> {
  const reuseDays = factoryVisitSessionStorage.getCachedReuseDays(params.tenantId);
  const session = await factoryVisitSessionStorage.findReusableSessionForFactory({
    tenantId: params.tenantId,
    factoryId: params.factoryId,
    inspectorId: params.inspectorId,
    reuseDays,
  });
  return { reuseDays, session };
}

export async function fetchActiveFactoryVisitSession(params: {
  factoryId: string;
  tenantId: string;
  inspectorId: string;
  isOnline: boolean;
}): Promise<FactoryVisitSessionLookupResult> {
  if (!params.factoryId) {
    return { reuseDays: DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS, session: null };
  }
  if (!params.isOnline) {
    const offlineResult = await lookupOfflineSession(params);
    logFactoryVerificationDebug('session.lookupOffline', {
      factoryId: params.factoryId,
      reuseDays: offlineResult.reuseDays,
      sessionFound: Boolean(offlineResult.session),
    });
    return offlineResult;
  }
  const url = `${systemConfig.apiUrl}/${API_ENDPOINTS.INSPECTION.FACTORY_VISIT_SESSION_ACTIVE}?factoryId=${params.factoryId}`;
  const response = await clivApiRequest<ServerFactoryVisitSessionActiveResponse>({
    url,
    method: 'GET',
  });
  if (!response.ok || response.data == null) {
    logFactoryVerificationWarning('session.onlineLookupFailed', {
      factoryId: params.factoryId,
      status: response.status,
      requestId: response.requestId,
    });
    return lookupOfflineSession(params);
  }
  const reuseDays = response.data.reuseDays ?? DEFAULT_FACTORY_VISIT_SESSION_REUSE_DAYS;
  factoryVisitSessionStorage.cacheReuseDays(params.tenantId, reuseDays);
  if (!response.data.session) {
    logFactoryVerificationDebug('session.lookupOnline', {
      factoryId: params.factoryId,
      reuseDays,
      sessionFound: false,
    });
    return { reuseDays, session: null };
  }
  const session = await factoryVisitSessionStorage.upsertFromServer({
    tenantId: params.tenantId,
    inspectorId: params.inspectorId,
    serverSession: response.data.session,
  });
  logFactoryVerificationDebug('session.lookupOnline', {
    factoryId: params.factoryId,
    reuseDays,
    sessionFound: true,
    serverSessionId: session.serverSessionId,
  });
  return { reuseDays, session };
}
